import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { AppShell } from '../components/AppShell';
import { useConfirm } from '../components/ConfirmProvider';
import { useUserOrg } from '../lib/org';
import { useCurrentSite } from '../lib/currentSite';
import {
  listOrgDocuments,
  listSiteDocuments,
  markDocumentNotApplicable,
  unmarkDocumentNotApplicable,
  type DocumentRow,
} from '../lib/db';
import { cacheListByOrg } from '../lib/cache';
import { DOC_TEMPLATES, DOC_CATEGORIES, type DocCategory, type DocScope } from '../data/docTemplates';
import { isFeatureEnabled } from '../lib/features';

// Documents that should ALWAYS exist for any business — no opt-out button
// because legally / practically there is no scenario where they don't apply.
const ALWAYS_REQUIRED_TEMPLATES = new Set<string>([
  'hs_policy_statement',     // Health & Safety Policy — every business
  'subcontractor_pqq',        // PQQ — needed whenever you ever use any subbie
]);
import { Modal } from '../components/Modal';
import { SignaturePad, type SignaturePadHandle } from '../components/SignaturePad';
import { useRef } from 'react';
import { uploadDocument } from '../lib/storage';
import { generateDocumentPdf } from '../lib/pdf';
import { createDocument } from '../lib/db';
import { supabase } from '../lib/supabase';
import { LoadingIndicator } from '../components/LoadingIndicator';

type Tab = 'org' | 'site';

interface TemplateRowVM {
  templateId: string;
  title: string;
  category: DocCategory;
  scope: DocScope;
  description: string;
  current: DocumentRow | null;
  expiryRequired: boolean;
}

function statusPill(doc: DocumentRow | null, expiryRequired: boolean): { label: string; tone: string } {
  if (!doc) return { label: 'Not started', tone: 'pending' };
  if (doc.status === 'not_applicable') return { label: "Doesn't apply", tone: 'muted' };
  if (doc.status === 'expired') return { label: 'Expired', tone: 'danger' };
  if (doc.expires_at && new Date(doc.expires_at) < new Date()) return { label: 'Expired', tone: 'danger' };
  if (expiryRequired && doc.expires_at && (new Date(doc.expires_at).getTime() - Date.now()) < 30 * 86400000) {
    return { label: `v${doc.version} · expires soon`, tone: 'warn' };
  }
  return { label: `v${doc.version} · ${doc.status}`, tone: 'ok' };
}

export function DocumentsLibrary() {
  const { org } = useUserOrg();
  const { confirm } = useConfirm();
  const { currentSite } = useCurrentSite();
  const [tab, setTab] = useState<Tab>('org');
  const [categoryFilter, setCategoryFilter] = useState<DocCategory | 'all'>('all');
  const [search, setSearch] = useState('');
  const [orgDocs, setOrgDocs] = useState<DocumentRow[]>([]);
  const [siteDocs, setSiteDocs] = useState<DocumentRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [naTarget, setNaTarget] = useState<TemplateRowVM | null>(null);
  const [naReason, setNaReason] = useState('');
  const [naSubmitting, setNaSubmitting] = useState(false);

  const [bulkSignOpen, setBulkSignOpen] = useState(false);
  const [bulkSignatoryName, setBulkSignatoryName] = useState('');
  const [bulkSubmitting, setBulkSubmitting] = useState(false);
  const [bulkProgress, setBulkProgress] = useState({ done: 0, total: 0 });
  const bulkSigRef = useRef<SignaturePadHandle>(null);

  // Stale-while-revalidate on mount; post-mutation refreshes (after
  // bulk-sign, mark-NA, etc.) still call refresh() directly so the UI
  // reflects the actual server state.
  useEffect(() => {
    if (!org) return;
    let cancelled = false;
    let haveCache = false;
    cacheListByOrg<DocumentRow>('documents', org.org_id).then((cached) => {
      if (cancelled || cached.length === 0) return;
      haveCache = true;
      setOrgDocs(cached.filter((d) => !d.site_id));
      setSiteDocs(currentSite ? cached.filter((d) => d.site_id === currentSite.id) : []);
      setLoading(false);
    }).catch(() => {});
    Promise.all([
      listOrgDocuments(org.org_id),
      currentSite ? listSiteDocuments(currentSite.id) : Promise.resolve([]),
    ])
      .then(([o, s]) => { if (!cancelled) { setOrgDocs(o); setSiteDocs(s); setError(null); } })
      .catch((e) => { if (!cancelled) setError(e instanceof Error ? e.message : String(e)); })
      .finally(() => { if (!cancelled && !haveCache) setLoading(false); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [org, currentSite?.id]);

  const docs = tab === 'org' ? orgDocs : siteDocs;
  useEffect(() => {
    if (!bulkSignOpen) return;
    supabase.auth.getUser().then(({ data }) => {
      const name = (data.user?.user_metadata?.full_name as string | undefined) ?? '';
      if (name && !bulkSignatoryName) setBulkSignatoryName(name);
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bulkSignOpen]);

  const docsByTemplate = new Map<string, DocumentRow>();
  for (const d of docs) if (d.template_id) docsByTemplate.set(d.template_id, d);

  // Count of documents that have an actual file_path issued — used to show
  // the "Issued documents" banner that links to the flat list view.
  const issuedCount = [...orgDocs, ...siteDocs]
    .filter((d) => !!d.file_path && d.status !== 'not_applicable')
    .length;

  // List of org-wide policy templates not yet issued (and not marked not_applicable)
  const remainingOrgPolicies = DOC_TEMPLATES.filter((t) =>
    t.scope === 'org' && t.signatureRequired && t.category === 'policy' && !orgDocs.find((d) => d.template_id === t.id)
  );

  const cppEnabled = isFeatureEnabled(org, 'cpp');
  // f10Enabled feature flag dropped — the F10 template was removed because
  // the real F10 is filed on HSE's portal, not generated as a doc here.

  const rows: TemplateRowVM[] = DOC_TEMPLATES
    .filter((t) => t.scope === tab)
    .filter((t) => cppEnabled || t.id !== 'construction_phase_plan')
    .map((t) => ({
      templateId: t.id,
      title: t.title,
      category: t.category,
      scope: t.scope,
      description: t.description,
      current: docsByTemplate.get(t.id) ?? null,
      expiryRequired: t.expiryRequired ?? false,
    }))
    .filter((r) => categoryFilter === 'all' || r.category === categoryFilter)
    .filter((r) => {
      if (!search.trim()) return true;
      const s = search.trim().toLowerCase();
      return r.title.toLowerCase().includes(s) || r.description.toLowerCase().includes(s);
    });

  async function refresh() {
    if (!org) return;
    setLoading(true);
    try {
      const [o, sList] = await Promise.all([
        listOrgDocuments(org.org_id),
        currentSite ? listSiteDocuments(currentSite.id) : Promise.resolve([]),
      ]);
      setOrgDocs(o);
      setSiteDocs(sList);
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }

  async function confirmNotRequired() {
    if (!org || !naTarget) return;
    setNaSubmitting(true);
    try {
      await markDocumentNotApplicable({
        orgId: org.org_id,
        siteId: naTarget.scope === 'site' ? (currentSite?.id ?? null) : null,
        templateId: naTarget.templateId,
        title: naTarget.title,
        category: naTarget.category,
        scope: naTarget.scope,
        reason: naReason,
      });
      setNaTarget(null);
      setNaReason('');
      await refresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setNaSubmitting(false);
    }
  }

  async function undoNotRequired(docId: string) {
    if (!(await confirm({
      title: 'Restore this template?',
      body: 'It can be filled in again.',
      confirmLabel: 'Restore',
    }))) return;
    try {
      await unmarkDocumentNotApplicable(docId);
      await refresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }

  async function bulkSignAll(e: React.FormEvent) {
    e.preventDefault();
    if (!org) return;
    if (!bulkSignatoryName.trim()) { setError('Signatory name is required.'); return; }
    if (bulkSigRef.current?.isEmpty()) { setError('Sign in the box.'); return; }
    setBulkSubmitting(true);
    setBulkProgress({ done: 0, total: remainingOrgPolicies.length });
    setError(null);
    try {
      // Capture signature once and re-use across every policy
      const blob = await bulkSigRef.current!.toBlob();
      const sigDataUrl = await new Promise<string>((resolve, reject) => {
        const r = new FileReader();
        r.onload = () => resolve(String(r.result));
        r.onerror = () => reject(new Error('Failed to read signature'));
        r.readAsDataURL(blob);
      });
      const dateStr = new Date().toLocaleDateString('en-GB', { day: '2-digit', month: 'long', year: 'numeric' });
      let done = 0;
      for (const tpl of remainingOrgPolicies) {
        const docId = crypto.randomUUID();
        const fields = tpl.fields.map((f) => {
          if (f.key === 'company_name') return { key: f.key, label: f.label, value: org.org_name };
          if (f.key === 'director_name' || f.key === 'document_owner' || f.key === 'responsible_person' || f.key === 'fa_lead' || f.key === 'dpo')
            return { key: f.key, label: f.label, value: bulkSignatoryName.trim() };
          if (f.key === 'review_date') {
            const d = new Date(); d.setFullYear(d.getFullYear() + 1);
            return { key: f.key, label: f.label, value: d.toISOString().slice(0, 10) };
          }
          if (f.key === 'financial_year') {
            return { key: f.key, label: f.label, value: String(new Date().getFullYear()) };
          }
          return { key: f.key, label: f.label, value: '' };
        });
        const pdfBlob = await generateDocumentPdf({
          orgName: org.org_name,
          orgLogoPath: org.logo_path,
          title: tpl.title,
          category: tpl.category,
          scope: tpl.scope,
          siteName: null,
          fields,
          sections: tpl.sections.map((sec) => ({ heading: sec.heading, body: sec.body })),
          signatureDataUrl: sigDataUrl,
          signatoryName: bulkSignatoryName.trim(),
          date: dateStr,
          notes: 'Issued via bulk sign — using the standard template text.',
        });
        const fileName = `${tpl.id}-v1-${dateStr.replace(/\s+/g, '-')}.pdf`;
        const file = new File([pdfBlob], fileName, { type: 'application/pdf' });
        const path = await uploadDocument(org.org_id, docId, file);
        await createDocument({
          id: docId,
          orgId: org.org_id,
          siteId: null,
          templateId: tpl.id,
          title: tpl.title,
          category: tpl.category,
          scope: tpl.scope,
          status: 'issued',
          filePath: path,
          fileName,
          mimeType: 'application/pdf',
          sizeBytes: file.size,
          notes: 'Issued via bulk sign — using the standard template text.',
        });
        done += 1;
        setBulkProgress({ done, total: remainingOrgPolicies.length });
      }
      setBulkSignOpen(false);
      await refresh();
    } catch (err) {
      console.error('[bulkSignAll]', err);
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBulkSubmitting(false);
    }
  }

      return (
    <AppShell title="Documents">
      {issuedCount > 0 && (
        <Link
          to="/documents/issued"
          className="rams-list-banner rams-list-banner-link"
          aria-label={`View all ${issuedCount} issued documents`}
          style={{ marginBottom: 12 }}
        >
          <div>
            <span className="rams-list-banner-title">Issued documents</span>
            <span className="rams-list-banner-meta">{issuedCount} on file · tap to view or email</span>
          </div>
          <span className="rams-list-banner-chev" aria-hidden="true">›</span>
        </Link>
      )}

      <div className="doc-scope-tiles" role="tablist" aria-label="Document scope">
        <button
          type="button" role="tab" aria-selected={tab === 'org'}
          className={`doc-scope-tile ${tab === 'org' ? 'active' : ''}`}
          onClick={() => setTab('org')}
        >
          <span className="doc-scope-icon" aria-hidden>
            <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
              <path d="M3 21h18" />
              <path d="M5 21V8l7-4 7 4v13" />
              <path d="M9 21v-6h6v6" />
              <path d="M9 11h.01M12 11h.01M15 11h.01" />
            </svg>
          </span>
          <span className="doc-scope-title">Org-wide</span>
          <span className="doc-scope-sub">Policies, certs, training</span>
          <span className="doc-scope-count">{orgDocs.length} issued</span>
        </button>
        <button
          type="button" role="tab" aria-selected={tab === 'site'}
          className={`doc-scope-tile ${tab === 'site' ? 'active' : ''}`}
          onClick={() => setTab('site')}
        >
          <span className="doc-scope-icon" aria-hidden>
            <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 1 1 18 0z" />
              <circle cx="12" cy="10" r="3" />
            </svg>
          </span>
          <span className="doc-scope-title">This site</span>
          <span className="doc-scope-sub">{currentSite ? currentSite.name : 'Select a site first'}</span>
          <span className="doc-scope-count">{siteDocs.length} issued</span>
        </button>
      </div>

      {tab === 'site' && !currentSite && (
        <div className="banner banner-warn" style={{ marginTop: 12 }}>
          Select a site first — no current site is set.
        </div>
      )}

      {tab === 'org' && remainingOrgPolicies.length > 1 && (
        <div className="card" style={{ background: 'var(--brand-pale)', borderColor: 'var(--brand-light)', marginBottom: 12 }}>
          <p style={{ margin: '0 0 6px', fontWeight: 600 }}>
            {remainingOrgPolicies.length} policies still to sign
          </p>
          <p className="muted" style={{ margin: '0 0 10px', fontSize: 13 }}>
            Use the standard text and sign once — covers all remaining org-wide policies in one go.
            You can re-issue any of them individually later if you change the wording.
          </p>
          <button
            type="button"
            className="btn btn-primary btn-sm"
            onClick={() => setBulkSignOpen(true)}
          >
            Use standard text + sign all
          </button>
        </div>
      )}

      <div className="filter-bar" style={{ display: 'flex', gap: 8, marginTop: 12, flexWrap: 'wrap' }}>
        <input
          type="search"
          placeholder="Search documents…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{ flex: '1 1 200px' }}
        />
        <select
          value={categoryFilter}
          onChange={(e) => setCategoryFilter(e.target.value as DocCategory | 'all')}
        >
          <option value="all">All categories</option>
          {DOC_CATEGORIES.map((c) => (
            <option key={c.id} value={c.id}>{c.label}</option>
          ))}
        </select>
      </div>

      {error && (
        <div className="banner banner-error" style={{ marginTop: 12, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
          <span>{error}</span>
          <button type="button" className="btn btn-ghost btn-sm" onClick={() => { setError(null); refresh(); }}>
            Try again
          </button>
        </div>
      )}
      {loading && <LoadingIndicator />}

      <div className="doc-list" style={{ marginTop: 12 }}>
        {rows.map((row) => {
          const pill = statusPill(row.current, row.expiryRequired);
          const fillHref = tab === 'site' && currentSite
            ? `/documents/site/${currentSite.id}/template/${row.templateId}`
            : `/documents/org/template/${row.templateId}`;
          return (
            <div key={row.templateId} className="card doc-row" style={{ marginBottom: 8 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                    <strong>{row.title}</strong>
                    <span className={`status-pill status-${pill.tone}`}>{pill.label}</span>
                    <span className="muted" style={{ fontSize: 11, textTransform: 'uppercase' }}>{row.category}</span>
                  </div>
                  <p className="muted" style={{ margin: '4px 0 0', fontSize: 13 }}>{row.description}</p>
                  {row.current?.status === 'not_applicable' && row.current.notes && (
                    <p className="muted" style={{ margin: '4px 0 0', fontSize: 12, fontStyle: 'italic' }}>
                      {row.current.notes}
                    </p>
                  )}
                  {row.current && (
                    <p className="muted" style={{ margin: '4px 0 0', fontSize: 12 }}>
                      Issued {row.current.issued_at ? new Date(row.current.issued_at).toLocaleDateString('en-GB') : '—'}
                      {row.current.expires_at ? ` · expires ${new Date(row.current.expires_at).toLocaleDateString('en-GB')}` : ''}
                    </p>
                  )}
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {row.current && row.current.status === 'not_applicable' ? (
                    <button
                      type="button"
                      className="btn btn-ghost btn-sm"
                      onClick={() => row.current && undoNotRequired(row.current.id)}
                    >
                      Undo
                    </button>
                  ) : (
                    <>
                      {row.current ? (
                        <Link to={`/documents/${row.current.id}`} className="btn btn-ghost btn-sm">View</Link>
                      ) : null}
                      <Link to={fillHref} className="btn btn-primary btn-sm">
                        {row.current ? 'New version' : 'Fill in'}
                      </Link>
                      {!row.current && !ALWAYS_REQUIRED_TEMPLATES.has(row.templateId) && (
                        <button
                          type="button"
                          className="btn-link-sm"
                          onClick={() => { setNaTarget(row); setNaReason(''); }}
                          title="Only tick if you're sure — e.g. F10 isn't needed for jobs under 30 days, CPP isn't needed if you're not the Principal Contractor"
                        >
                          Doesn't apply here
                        </button>
                      )}
                    </>
                  )}
                </div>
              </div>
            </div>
          );
        })}
        {!loading && rows.length === 0 && (
          <p className="muted">No documents match your filter.</p>
        )}
      </div>
    <Modal open={bulkSignOpen} title={`Issue ${remainingOrgPolicies.length} policies in one go`} onClose={() => !bulkSubmitting && setBulkSignOpen(false)}>
      <p className="muted" style={{ margin: '0 0 12px', fontSize: 13 }}>
        We'll generate a v1 PDF for each remaining policy using the standard text, splice in your company name, and sign with the signature below. You can re-issue any individually later.
      </p>
      <div className="card" style={{ marginBottom: 12, background: 'var(--brand-pale)' }}>
        <p style={{ margin: 0, fontWeight: 600 }}>Will be issued:</p>
        <ul className="muted" style={{ paddingLeft: 18, fontSize: 12, margin: '6px 0 0' }}>
          {remainingOrgPolicies.slice(0, 8).map((t) => (<li key={t.id}>{t.title}</li>))}
          {remainingOrgPolicies.length > 8 && (<li>… and {remainingOrgPolicies.length - 8} more</li>)}
        </ul>
      </div>
      <form onSubmit={bulkSignAll}>
        <div className="field needs-input">
          <label htmlFor="bulk-name">Signatory name *</label>
          <input
            id="bulk-name"
            type="text"
            value={bulkSignatoryName}
            onChange={(e) => setBulkSignatoryName(e.target.value)}
            required
          />
        </div>
        <SignaturePad ref={bulkSigRef} label="Sign once for all policies" />
        {bulkSubmitting && (
          <div style={{ margin: '12px 0' }}>
            <p className="muted" style={{ margin: '0 0 4px', fontSize: 13 }}>Issuing… {bulkProgress.done} of {bulkProgress.total}</p>
            <div className="cpp-progress-bar"><div className="cpp-progress-fill" style={{ width: `${(bulkProgress.done / Math.max(1, bulkProgress.total)) * 100}%` }} /></div>
          </div>
        )}
        <div className="btn-stack">
          <button type="submit" className="btn btn-primary" disabled={bulkSubmitting}>
            {bulkSubmitting ? 'Issuing…' : `Issue ${remainingOrgPolicies.length} policies`}
          </button>
          <button type="button" className="btn btn-ghost" onClick={() => setBulkSignOpen(false)} disabled={bulkSubmitting}>
            Cancel
          </button>
        </div>
      </form>
    </Modal>

    {naTarget && (
        <div className="modal-backdrop" onClick={() => !naSubmitting && setNaTarget(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3 style={{ marginTop: 0 }}>Mark "{naTarget.title}" as not required?</h3>
            <p className="muted" style={{ marginTop: 0, fontSize: 14 }}>
              {naTarget.scope === 'site'
                ? `This will hide the template for ${currentSite?.name ?? 'this site'}.`
                : 'This will hide the template across the whole organisation.'}
              {' '}You can undo this any time.
            </p>
            <div className="field">
              <label htmlFor="na-reason">Reason (optional)</label>
              <input
                id="na-reason"
                type="text"
                placeholder="e.g. We're not the Principal Contractor on this site"
                value={naReason}
                onChange={(e) => setNaReason(e.target.value)}
              />
            </div>
            <div className="btn-stack">
              <button
                type="button"
                className="btn btn-primary"
                onClick={confirmNotRequired}
                disabled={naSubmitting}
              >
                {naSubmitting ? 'Saving…' : 'Yes, mark as not required'}
              </button>
              <button
                type="button"
                className="btn btn-ghost"
                onClick={() => setNaTarget(null)}
                disabled={naSubmitting}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </AppShell>
  );
}
